import React, { useEffect, useState, memo } from 'react';
import { Shield, Bug, Key, Database, Globe, Server, Users, Lock, Wifi, Terminal, Search, Zap, Brain, AlertTriangle, CheckCircle } from 'lucide-react';
import './LLMSchema.css';

// Messages IA dynamiques selon le contexte
const AI_MESSAGES = {
  idle: [
    "Analyse des menaces en cours...",
    "Surveillance active du réseau",
    "Corrélation des données de sécurité",
    "Modèle IA prêt pour l'analyse",
    "Monitoring en temps réel actif"
  ],
  scanning: [
    "Scan de vulnérabilités en cours",
    "Analyse des ports ouverts...",
    "Détection des services exposés",
    "Évaluation des risques en cours"
  ],
  found_vulns: [
    "Vulnérabilités détectées !",
    "Analyse des failles critiques",
    "Priorisation des risques",
    "Recommandations en cours..."
  ],
  completed: [
    "Analyse terminée avec succès",
    "Rapport de sécurité généré",
    "Synthèse des résultats prête"
  ]
};

// Outils d'attaque par défaut
const DEFAULT_ATTACK_TOOLS = [
  { icon: Search, title: "Reconnaissance", sub: "Nmap, Shodan, OSINT", active: false },
  { icon: Bug, title: "Exploitation", sub: "Metasploit, SQLMap", active: false },
  { icon: Key, title: "Brute Force", sub: "Hydra, John the Ripper", active: false },
  { icon: Terminal, title: "Post-Exploitation", sub: "Élévation de privilèges", active: false }
];

// Cibles par défaut
const DEFAULT_TARGETS = [
  { icon: Globe, title: "Applications Web", sub: "Ports 80, 443, 8080", status: "idle" },
  { icon: Server, title: "Serveurs", sub: "SSH, RDP, SMB", status: "idle" },
  { icon: Database, title: "Bases de données", sub: "MySQL, PostgreSQL", status: "idle" },
  { icon: Wifi, title: "Réseau", sub: "Routeurs, switches", status: "idle" }
];

// Composant de particules statiques - rendu une seule fois, jamais re-rendu
const StaticParticles = memo(() => (
  <>
    {/* 6 particules par rangée avec des délais CSS différents */}
    {[1, 2, 3, 4, 5, 6].map(i => (
      <span key={i} className={`sy-particle sy-particle-${i}`} />
    ))}
  </>
));
StaticParticles.displayName = 'StaticParticles';

// Composant PipeRow mémorisé pour éviter les re-renders
const PipeRow = memo(({ rowNum, direction }) => (
  <div className="sy-pipe-row" data-row={rowNum}>
    <div className={`sy-pipe-line sy-pipe-${direction}`}></div>
    <StaticParticles />
  </div>
));
PipeRow.displayName = 'PipeRow';

const LLMSchema = ({
  attackTools = DEFAULT_ATTACK_TOOLS,
  targets = DEFAULT_TARGETS,
  scanStatus = 'idle', // 'idle', 'scanning', 'found_vulns', 'completed'
  stats = null,
  isActive = true, // Si la page est active (visible)
  cpuUsage = 0, // CPU usage 0-100%
  gpuUsage = 0  // GPU usage 0-100%
}) => {
  const [currentMessage, setCurrentMessage] = useState(AI_MESSAGES.idle[0]);

  // Changement de message dynamique
  useEffect(() => {
    if (!isActive) return;

    const messages = AI_MESSAGES[scanStatus] || AI_MESSAGES.idle;
    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setCurrentMessage(messages[index]);
    }, 4000);

    return () => clearInterval(interval);
  }, [isActive, scanStatus]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'scanning': return 'text-yellow-400 animate-pulse';
      case 'vulnerable': return 'text-red-400';
      case 'secure': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scanning': return <Zap className="w-3 h-3 animate-pulse" />;
      case 'vulnerable': return <AlertTriangle className="w-3 h-3" />;
      case 'secure': return <CheckCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  return (
    <div className="sy-llm-schema sy-llm-schema-wide">
      <div className="sy-llm-grid">
        {/* Colonne gauche : outils d'attaque */}
        <div className="sy-side sy-side-left">
          <div className="sy-side-header">
            <Terminal className="w-4 h-4 text-brand-green" />
            <span>Outils d'Attaque</span>
          </div>
          {attackTools.map((tool, idx) => {
            const IconComponent = tool.icon;
            return (
              <div key={idx} className={`sy-side-card ${tool.active ? 'sy-card-active' : ''}`}>
                <div className="sy-card-icon">
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="sy-card-content">
                  <div className="sy-side-card-title">{tool.title}</div>
                  <div className="sy-side-card-sub">{tool.sub}</div>
                </div>
                {tool.active && (
                  <div className="sy-card-status active">
                    <Zap className="w-3 h-3" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Centre : IA LLM */}
        <div className="sy-center-area">
          {/* Tuyaux avec effet neural - particules statiques CSS */}
          <div className="sy-pipes sy-pipes-left">
            <PipeRow rowNum={1} direction="from-left" />
            <PipeRow rowNum={2} direction="from-left" />
            <PipeRow rowNum={3} direction="from-left" />
            <PipeRow rowNum={4} direction="from-left" />
          </div>

          {/* Coeur IA avec animation améliorée */}
          <div className="sy-core-wrapper">
            {/* Orbites supprimées selon demande utilisateur */}
            <div className="sy-core-circle">
              <div className="sy-core-inner">
                <Brain className="sy-brain-icon" />
                <div className="sy-core-title">Moteur IA LLM</div>
                <div className="sy-core-sub sy-message-dynamic">
                  {currentMessage}
                </div>
              </div>
            </div>
          </div>

          <div className="sy-pipes sy-pipes-right">
            <PipeRow rowNum={1} direction="to-right" />
            <PipeRow rowNum={2} direction="to-right" />
            <PipeRow rowNum={3} direction="to-right" />
            <PipeRow rowNum={4} direction="to-right" />
          </div>
        </div>

        {/* Colonne droite : cibles */}
        <div className="sy-side sy-side-right">
          <div className="sy-side-header">
            <Shield className="w-4 h-4 text-brand-green" />
            <span>Cibles Analysées</span>
          </div>
          {targets.map((target, idx) => {
            const IconComponent = target.icon;
            return (
              <div key={idx} className={`sy-side-card ${target.status !== 'idle' ? 'sy-card-' + target.status : ''}`}>
                <div className="sy-card-icon">
                  <IconComponent className="w-5 h-5" />
                </div>
                <div className="sy-card-content">
                  <div className="sy-side-card-title">{target.title}</div>
                  <div className="sy-side-card-sub">{target.sub}</div>
                </div>
                {target.status !== 'idle' && (
                  <div className={`sy-card-status ${target.status}`}>
                    {getStatusIcon(target.status)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats en temps réel si disponibles */}
      {stats && (
        <div className="sy-stats-bar">
          <div className="sy-stat-item">
            <span className="sy-stat-label">Scans</span>
            <span className="sy-stat-value">{stats.totalScans || 0}</span>
          </div>
          <div className="sy-stat-divider"></div>
          <div className="sy-stat-item">
            <span className="sy-stat-label">Vulnérabilités</span>
            <span className="sy-stat-value sy-stat-warning">{stats.totalVulnerabilities || 0}</span>
          </div>
          <div className="sy-stat-divider"></div>
          <div className="sy-stat-item">
            <span className="sy-stat-label">Critiques</span>
            <span className="sy-stat-value sy-stat-critical">{stats.criticalVulnerabilities || 0}</span>
          </div>
        </div>
      )}

      {/* Panneau de texte en bas */}
      <div className="sy-bottom-panel" style={{ maxWidth: '100%' }}>
        <div className="sy-bottom-glow"></div>
        <span className="sy-highlight">L'IA LLM SYLink</span> analyse en continu vos systèmes,
        détecte les vulnérabilités et corrèle les menaces en temps réel. Elle transforme
        les données brutes en recommandations actionnables pour protéger votre infrastructure.
      </div>
    </div>
  );
};

export default LLMSchema;
